import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import useWorkspace from '@webclient/hooks/useWorkspace';

import { WorkspaceResponseType } from '@webclient/shared-types/workspace';

import useGetWorkspaces from '@webclient/layouts/main/sections/toolbar/hooks/useGetAllWorkspacesById';
import paths from '@webclient/router/paths';

type WorkspaceSelectionContextType = {
  selectedWorkspaceId: string | null;
  selectedWorkspaceData: WorkspaceResponseType | null;
  handleNavigateToSelectedWorkspace: VoidFunction;
  handleSetSelectedWorkspaceId: (id: string) => void;
};

export const WorkspaceSelectionContext =
  createContext<WorkspaceSelectionContextType | null>(null);

export default function WorkspaceSelectionProvider({
  children,
}: React.PropsWithChildren) {
  const { workspace } = useWorkspace();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    workspace?.id || null,
  );

  const [selectedWorkspaceData, setSelectedWorkspaceData] =
    useState<WorkspaceResponseType | null>(null);

  const { workspaceData } = useGetWorkspaces();
  const navigateTo = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => setSelectedWorkspaceId(workspace?.id || null), [workspace]);

  const handleNavigateToSelectedWorkspace = useCallback(() => {
    if (pathname !== paths.knowledgeBase.pathname) {
      navigateTo(paths.knowledgeBase.pathname);
    }
  }, [navigateTo, pathname]);

  const handleSetSelectedWorkspaceId = useCallback(
    (id: string) => {
      setSelectedWorkspaceId(id);

      const selectedWorkspace =
        workspaceData?.find((workspace) => workspace?.id === id) || null;

      setSelectedWorkspaceData(selectedWorkspace);
    },
    [workspaceData],
  );

  const memoizedValue = useMemo(
    () => ({
      handleNavigateToSelectedWorkspace,
      handleSetSelectedWorkspaceId,
      selectedWorkspaceId,
      selectedWorkspaceData,
    }),
    [
      handleNavigateToSelectedWorkspace,
      selectedWorkspaceId,
      selectedWorkspaceData,
      handleSetSelectedWorkspaceId,
    ],
  );

  return (
    <WorkspaceSelectionContext.Provider value={memoizedValue}>
      {children}
    </WorkspaceSelectionContext.Provider>
  );
}
